import React, { useMemo } from 'react';
import { Bounds } from '../types';
import { createScaledOffsets, InverseScales } from '../utils/scales';
import {
  ScrollMapsPageFoldMarker,
  ScrollMapsPageFoldMarkerVisibilityState,
} from '../ScrollMapsPageFoldMarker';

const MarkerWidth = 150;
const MarkerHeight = 20;

export interface ScrollMapsPageFoldMarkerContainer {
  /**
   * The bounds for the playback container
   */
  playbackContainerBounds: Bounds;
  /**
   * The bounds for the page itself
   */
  pageBounds: Bounds;
  /**
   * If the medium fold is being displayed inside an internal scroll element.
   */
  internalScroll?: boolean;
  /**
   * The container bounds of the internally scrolling element.
   */
  internalScrollContainerBounds?: Bounds;
  /**
   * The viewport of the internally scrolling element.
   */
  internalScrollViewportBounds?: Bounds;
  /**
   * The inverse scales to be applied to undo any scaling applied by playback.
   */
  inverseScales: InverseScales;
  /**
   * The median viewport height of users upon first loading the page (in px).
   */
  medianPageFold: number;
  /**
   * The zIndex offset for this marker. The marker's index is pageBounds.zIndex + zIndexOffset.
   */
  zIndexOffset: number;
}

/**
 * The controller for the Scroll Maps Marker responsible for displaying page fold information.
 */
export const ScrollMapsPageFoldMarkerContainer: React.FC<ScrollMapsPageFoldMarkerContainer> = ({
  playbackContainerBounds,
  pageBounds,
  inverseScales,
  internalScroll,
  internalScrollContainerBounds = { top: 0 },
  internalScrollViewportBounds = {
    top: 0,
    bottom: 0,
    height: 0,
  },
  medianPageFold,
  zIndexOffset,
}) => {
  // If the marker is visible on the screen, display it at the appropriate point. If it is offscreen, display a Ghost
  // Marker at the top/bottom of the screen to signal that it is there.
  const [position, visibilityState, adjustedMarkerWidth] = useMemo((): [
    number,
    ScrollMapsPageFoldMarkerVisibilityState,
    number,
  ] => {
    const elementTop = internalScroll ? internalScrollContainerBounds.top : pageBounds.top;

    // Median fold is for the page but if a static header and/or footer exists, we need to adjust the position of it
    // since users see the header and footer at all times. E.g., 1000px viewport with 100px header and 100px footer
    // should adjust median fold to 800px inside internal scrolling element.
    let adjustedMedianFold = medianPageFold;
    if (internalScroll) {
      adjustedMedianFold =
        medianPageFold -
        internalScrollViewportBounds.top - // Adjust for any header
        (pageBounds.bottom === 0 ? 0 : pageBounds.bottom - internalScrollViewportBounds.bottom); // Adjust for any footer
    }

    // Position is the absolute position on the page and not just the scrollable element. We need to take into
    // consideration that an internal scroll page may have a header or footer.
    let position = adjustedMedianFold + elementTop;
    let visibilityState: ScrollMapsPageFoldMarkerVisibilityState = 'visible';
    let adjustedMarkerWidth = MarkerWidth;
    const scaledOffsets = createScaledOffsets(MarkerHeight, MarkerWidth, inverseScales);
    const highestPositionAllowed = internalScroll
      ? internalScrollViewportBounds.height
      : playbackContainerBounds.height;

    if (position < 0 + MarkerHeight / 2 - (scaledOffsets.y / 2) * inverseScales.y) {
      position = MarkerHeight * inverseScales.y;
      visibilityState = 'offscreen_above';
      adjustedMarkerWidth += 20;
    } else if (
      position >
      highestPositionAllowed - MarkerHeight / 2 + (scaledOffsets.y / 2) * inverseScales.y
    ) {
      position = highestPositionAllowed - MarkerHeight * inverseScales.y;
      visibilityState = 'offscreen_below';
      adjustedMarkerWidth += 20;
    }
    return [position, visibilityState, adjustedMarkerWidth];
  }, [
    medianPageFold,
    pageBounds,
    inverseScales,
    playbackContainerBounds,
    internalScrollViewportBounds,
    internalScrollContainerBounds,
  ]);

  // Place the top such that the center of the marker is at position. The center of the marker remains the same during scaling.
  // Hence, no offset is necessary.
  const markerTop = position - MarkerHeight / 2;

  // Place the marker in the center of the screen.
  const markerLeft = (pageBounds.right - pageBounds.left) / 2 - adjustedMarkerWidth / 2;
  const scaledOffsets = createScaledOffsets(MarkerHeight, adjustedMarkerWidth, inverseScales);
  return (
    // eslint-disable-next-line @fullstory/annotate-react/data-component
    <ScrollMapsPageFoldMarker
      inverseScales={inverseScales}
      markerBoxBounds={{
        top: markerTop,
        bottom: markerTop + MarkerHeight * inverseScales.y + scaledOffsets.y * inverseScales.y,
        left: markerLeft,
        right:
          markerLeft + adjustedMarkerWidth * inverseScales.x + scaledOffsets.x * inverseScales.x,
        height: MarkerHeight,
        width: adjustedMarkerWidth,
        zIndex: pageBounds.zIndex + zIndexOffset,
      }}
      medianPageFold={medianPageFold}
      visibilityState={visibilityState}
    />
  );
};
